import {
  type CSSProperties,
  memo,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import { useResizeDetector } from 'react-resize-detector';
import { List, type ListRowRenderer, WindowScroller, type WindowScrollerProps } from 'react-virtualized';

import { type TableRowType } from '~/components/TableView/basicTypes';
import { useListHorizontalScroll } from '~/hooks/useListHorizontalScroll';
import usePrevious from '~/hooks/usePrevious';
import { getScrollContainer } from './ui';

export type TableViewProps = {
  rows: TableRowType[];
  renderHeaders?: (ref: RefObject<HTMLDivElement>) => ReactNode;
  minTableWidth: number;
  scrollElement?: HTMLDivElement | HTMLElement | null;
  /**
   * @deprecated Use `scrollElement` instead.
   */
  scrollElementRef?: RefObject<HTMLDivElement | HTMLElement | null>;
  canScrollHorizontally?: boolean;
};

// add small top/bottom offset to row to avoid focus/drop border cut
export const ROW_TOP_OFFSET = 6;
export const ROW_BOTTOM_OFFSET = 6;

export const TABLE_HEADER_HEIGHT = 56;

// NOTE: `24` correlates with the px-2 value in `components/TableView/TableRow/DesktopTableRowContainer.tsx`
// where `px-2` equates to `pr-2` and `pl-2`, giving a total of space of 24px
// The classes and pixel values must match exactly for the table contents to avoid horizontally over/under-scrolling
const TABLE_WIDTH_PADDING = 24;
const TABLE_MARGIN_OFFSET = 16;

/**
 * This is a basic component to display table view
 */
export const TableView = memo(
  ({
    rows,
    minTableWidth,
    renderHeaders,
    canScrollHorizontally = true,
    scrollElement: scrollElementProp,
    scrollElementRef,
  }: TableViewProps) => {
    const { width = 0, ref: widthRef } = useResizeDetector({
      handleHeight: false,
    });
    const [scrollElement, setScrollElement] = useState<WindowScrollerProps['scrollElement']>(undefined);

    useEffect(() => {
      const element = scrollElementProp ?? scrollElementRef?.current ?? getScrollContainer();
      setScrollElement(element || undefined);
    }, [scrollElementProp, scrollElementRef]);

    const tableWidth = useMemo(
      () => Math.max(width + TABLE_MARGIN_OFFSET, minTableWidth) + TABLE_WIDTH_PADDING,
      [width, minTableWidth],
    );
    const totalHeight = useMemo(() => rows.reduce((total, row) => total + row.height, 0), [rows]);
    const previousHeight = usePrevious(totalHeight);

    const headerRef = useRef(null);
    const scrollRef = useRef(null);

    const listRef = useRef<List>(null);

    const onHorizontalScroll = useListHorizontalScroll({ listRef, scrollRef, headerRef });

    // recompute rows heights if total table height changes
    useEffect(() => {
      if (listRef.current && totalHeight !== previousHeight) {
        listRef.current.recomputeRowHeights();
      }
    }, [previousHeight, totalHeight]);

    const rowRenderer: ListRowRenderer = useCallback(
      ({ index, key, style: rowRenderStyle, isVisible }) => {
        const row = rows[index];

        // We need additional padding for the first element to avoid cutting focused outline
        const rowStyle = {
          ...rowRenderStyle,
          top: (rowRenderStyle.top as number) + ROW_TOP_OFFSET,
        };

        return row.render(rowStyle, key, isVisible);
      },
      [rows],
    );

    // NOTE: we add a bottom offset to the last row to avoid cutting focused outline
    // when dragging row items to create a version stack or move assets to a board
    const getRowHeight = useCallback(
      ({ index }: { index: number }) => rows[index].height + (index === rows.length - 1 ? ROW_BOTTOM_OFFSET : 0),
      [rows],
    );

    const tableStyle: CSSProperties = useMemo(
      () => ({
        paddingBottom: canScrollHorizontally ? 54 : '16rem',
        visibility: width === 0 ? 'hidden' : 'visible',
      }),
      [canScrollHorizontally, width],
    );

    /**
     * It's important to have the scrollElement check here because we need all the children to mount
     * at the same time to ensure that the table scroll sync is working properly.
     */
    if (!scrollElement) {
      return null;
    }

    return (
      <>
        {renderHeaders?.(headerRef)}

        <div className="w-full" ref={widthRef} />
        {!!tableWidth && (
          <WindowScroller scrollElement={scrollElement}>
            {({ height = 0, scrollTop, isScrolling }) => (
              <List
                tabIndex={-1}
                ref={listRef}
                autoWidth
                autoHeight
                width={tableWidth}
                height={height}
                rowRenderer={rowRenderer}
                isScrolling={isScrolling}
                scrollTop={scrollTop}
                rowCount={rows.length}
                rowHeight={getRowHeight}
                style={tableStyle}
              />
            )}
          </WindowScroller>
        )}
        <div
          style={{ width: '100%', height: 0, overflowX: 'auto', visibility: 'hidden', pointerEvents: 'none' }}
          ref={scrollRef}
          onScroll={canScrollHorizontally ? onHorizontalScroll : undefined}
        />
      </>
    );
  },
  isEqual,
);

TableView.displayName = 'TableView';
